<template lang="pug">
.delivery-data-list
  p.delivery-data-list__empty(v-if="!entries.length") No hay entregas registradas.

  .delivery-data-list__item(v-for="(entry, index) in entries" :key="entryKey(entry, index)")
    .delivery-data-list__item-header
      .delivery-data-list__item-title-row
        span.delivery-data-list__item-title Guía {{ entry.delivery || entries.length - index }}
        .tag.delivery-data-list__delivery-status(:style="deliveryStatusTagStyle(entry)") {{ statusLabel(entry.status) }}
      span.delivery-data-list__item-date(v-if="entry.createdAt") {{ dateName(entry.createdAt) }} - {{ time(entry.createdAt) }}

    .delivery-data-list__body
      .delivery-data-list__field
        span.delivery-data-list__label Nombre
        span.delivery-data-list__value.capitalize(:class="valueClass(entry, 'name', index)") {{ entry.nameRecipient || '—' }}

      .delivery-data-list__field
        span.delivery-data-list__label Teléfono
        span.delivery-data-list__value(:class="valueClass(entry, 'phone', index)") {{ phoneLabel(entry) }}

      .delivery-data-list__field
        span.delivery-data-list__label Ubicación
        span.delivery-data-list__value.capitalize(:class="valueClass(entry, 'location', index)") {{ locationLabel(entry) }}

      .delivery-data-list__field
        span.delivery-data-list__label Dirección
        span.delivery-data-list__value.capitalize(:class="valueClass(entry, 'address', index)") {{ entry.addressRecipient || '—' }}

    .delivery-data-list__meta
      .delivery-data-list__field(v-if="entry.tracking")
        span.delivery-data-list__label Guía
        span.delivery-data-list__value
          template(v-if="entry.trackingCancelled")
            | {{ entry.tracking }}
            span.delivery-data-list__tracking-cancelled  (anulada)
          template(v-else) {{ entry.tracking }}

      .delivery-data-list__field(v-if="freightCost(entry) != null")
        span.delivery-data-list__label Flete
        span.delivery-data-list__value {{ money(freightCost(entry)) }}
        span.delivery-data-list__label(v-if="entry.conveyor === 'welivery' && !entry.weliverySettledMonth && !entry.trackingCancelled")  (provisional)
        span.delivery-data-list__label(v-if="entry.estimatedCost && !entry.cost")  (estimado, paga cliente)

      .delivery-data-list__field(v-if="entry.weliveryReceiptUrl")
        a(:href="entry.weliveryReceiptUrl" target="_blank" rel="noopener noreferrer") Ver comprobante de entrega

    .delivery-data-list__actions(v-if="canCancelTrackingDoc(entry) || canDeclineDeliveryDoc(entry)")
      shActionButton(
        v-if="canCancelTrackingDoc(entry)"
        :action="() => cancelTracking(entry)"
        text="Anular guía"
        textRunning="Anulando guía"
        :normalColor="true"
        :textConfirmation="`Se anulará la guía ${entry.tracking} de la entrega ${entry.delivery}`"
      )
      shActionButton(
        v-if="canDeclineDeliveryDoc(entry)"
        :action="() => declineDelivery(entry)"
        text="Declinar entrega"
        textRunning="Declinando"
        :normalColor="true"
        :textConfirmation="`Se declinará la entrega ${entry.delivery}`"
      )
</template>

<script>
import mFilters from '@/mixins/filters'
import shActionButton from '@/components/elements/ActionButton'
import {
  canCancelTrackingDoc,
  canDeclineDeliveryDoc,
  deliveryFieldDiffers,
  getDeliveryReferenceForEntry
} from '@/helpers/deliveryMass'

export default {
  name: 'DeliveryDataList',

  mixins: [mFilters],

  components: { shActionButton },

  props: {
    record: {
      type: Object,
      default: null
    },
    onCancelTracking: {
      type: Function,
      default: null
    },
    onDeclineDelivery: {
      type: Function,
      default: null
    }
  },

  data () {
    return {
      statusSpanish: {
        created: 'Creada',
        inprogress: 'En progreso',
        delivered: 'Entregada',
        declined: 'Declinada',
        paid: 'Pagada',
        collected: 'Recaudada'
      }
    }
  },

  computed: {
    entries () {
      return ((this.record && this.record.deliveries) || []).slice().reverse()
    }
  },

  methods: {
    canCancelTrackingDoc,
    canDeclineDeliveryDoc,

    entryKey (entry, index) {
      return `${entry.delivery || ''}-${index}`
    },

    statusLabel (status) {
      return this.statusSpanish[status] || status || '—'
    },

    deliveryStatusTagStyle (entry) {
      const status = entry && entry.status
      const colors = this.colorsStatus[status]
      if (!colors) return {}
      return {
        background: colors.background,
        color: colors.color
      }
    },

    phoneLabel (entry) {
      if (!entry.phonesRecipient || !entry.phonesRecipient.length) return '—'
      return entry.phonesRecipient.join(', ')
    },

    locationLabel (entry) {
      const parts = [entry.locationRecipient, entry.departmentRecipient].filter(Boolean)
      return parts.length ? parts.join(' - ') : '—'
    },

    freightCost (entry) {
      if (entry.estimatedCost != null && !entry.cost) return entry.estimatedCost
      if (entry.cost != null && entry.cost !== '') return entry.cost
      return null
    },

    costLabel (entry) {
      return this.money(this.freightCost(entry))
    },

    valueClass (entry, field, displayIndex) {
      const reference = getDeliveryReferenceForEntry(this.record, displayIndex)

      return {
        'delivery-data-list__value--outdated': deliveryFieldDiffers(entry, reference, field)
      }
    },

    cancelTracking (entry) {
      if (!this.onCancelTracking) return true
      return this.onCancelTracking(entry)
    },

    declineDelivery (entry) {
      if (!this.onDeclineDelivery) return true
      return this.onDeclineDelivery(entry)
    }
  }
}
</script>

<style lang="stylus" scoped>
.delivery-data-list
  display flex
  flex-direction column
  gap 2rem

.delivery-data-list__empty
  margin 0
  font-size .95rem
  opacity .7

.delivery-data-list__item
  padding 0

.delivery-data-list__item-header
  display flex
  flex-wrap wrap
  align-items baseline
  justify-content space-between
  gap .5rem
  margin-bottom .75rem

.delivery-data-list__item-title-row
  display flex
  flex-wrap wrap
  align-items center
  gap .5rem
  min-width 0

.delivery-data-list__item-title
  font-size 1rem
  font-weight 600
  text-transform uppercase

.delivery-data-list__delivery-status
  flex-shrink 0
  text-transform capitalize

.delivery-data-list__item-date
  font-size .85rem
  color var(--color-placeholder)

.delivery-data-list__body
  padding .85rem 1rem
  background var(--color-light)
  border 1px solid var(--color-border)
  border-radius .35rem

.delivery-data-list__meta
  margin-top .85rem
  padding-top .85rem
  border-top 1px solid var(--color-border)

.delivery-data-list__field
  display grid
  grid-template-columns 6.5rem minmax(0, 1fr)
  gap .35rem .75rem
  margin-bottom .35rem
  font-size .95rem
  line-height 1.35

  &:last-child
    margin-bottom 0

.delivery-data-list__label
  color var(--color-placeholder)
  text-transform uppercase
  font-size .8rem

.delivery-data-list__value
  word-break break-word

.delivery-data-list__value--outdated
  color var(--color-red)
  text-decoration line-through

.delivery-data-list__tracking-cancelled
  color var(--color-red)

.delivery-data-list__actions
  display flex
  flex-wrap wrap
  gap .75rem
  margin-top .85rem
</style>



// WEBPACK FOOTER //
// src/components/elements/delivery/DeliveryDataList.vue